import { createElement } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { useOrgMe } from '@/api/organization';
import { OrgOverview } from './OrgOverview';
import { MembersPage } from './components/MembersPage';
import { PersonDetailPage } from './components/PersonDetailPage';
import { SubCustomersTab } from '@/pages/account/organization/components/SubCustomersTab';
import { AdminsTab } from '@/pages/account/organization/components/AdminsTab';
import { ActivityPage } from '@/pages/activity/ActivityPage';
import { TeamList } from '@/pages/teams/TeamList';
import { TeamDetail } from '@/pages/teams/TeamDetail';
import { ProjectList } from '@/pages/projects/ProjectList';
import { ProjectDetail } from '@/pages/projects/ProjectDetail';
import { buildOrgNavSections, orgActiveSlug } from './organizations-console-nav';
import { ORG_BASE } from './organizations-service';

/**
 * Organizations console — registers its sidebar in the app's ContextualSidebar
 * (scope-swap model, same as OSS/Healthcheck/Compute consoles).
 *
 * Sidebar is rendered in grouped sections (Organization / People / Workspaces /
 * Reseller) matching the spec §1.1. Reseller sections are hidden for non-resellers.
 *
 * Teams and Projects are now first-class sections inside this console. The old
 * special-case `onSelect` branches that navigated out to /teams and /projects
 * have been removed — they route in-console.
 *
 * TeamList / TeamDetail / ProjectList / ProjectDetail each bring their own
 * Container + PageHeader, so they are mounted WITHOUT an extra wrapping Container.
 */
export function OrganizationsConsole() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: me } = useOrgMe();

  const isReseller = !!me?.is_reseller;
  const isAdmin = !!me?.is_admin;

  const hrefFor = (slug: string) => `${ORG_BASE}/${slug}`;

  useSetContextualNav(() => {
    const sub = pathname.slice(ORG_BASE.length).replace(/^\//, '');
    const sections = buildOrgNavSections(isReseller);

    return {
      sections: sections.map((section) => ({
        heading: section.heading,
        items: section.entries.map((e) => ({
          id: hrefFor(e.slug),
          label: e.label,
          icon: createElement(e.icon, { size: 16 }),
          onSelect: () => navigate(hrefFor(e.slug)),
        })),
      })),
      activeId: hrefFor(orgActiveSlug(sub)),
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, navigate, isReseller]);

  return (
    <Routes>
      {/* Overview and contacts wrapped in a Container (these tabs are plain fragments/divs) */}
      <Route
        path="overview"
        element={
          <Container size="xl">
            <OrgOverview />
          </Container>
        }
      />
      <Route
        path="members"
        element={
          <Container size="xl">
            <MembersPage />
          </Container>
        }
      />
      <Route
        path="members/:personRef"
        element={
          <Container size="xl">
            <PersonDetailPage />
          </Container>
        }
      />
      {/* Contacts folded into Members — keep the path as a redirect for old links. */}
      <Route path="contacts" element={<Navigate to={`${ORG_BASE}/members`} replace />} />

      {/* Activity — org-wide audit log; ActivityPage brings its own Container */}
      <Route path="activity" element={<ActivityPage />} />

      {/* Teams — TeamList and TeamDetail bring their own Container + PageHeader */}
      <Route path="teams" element={<TeamList />} />
      <Route path="teams/:teamId/*" element={<TeamDetail />} />

      {/* Projects — ProjectList and ProjectDetail bring their own Container + PageHeader */}
      <Route path="projects" element={<ProjectList />} />
      <Route path="projects/:projectId/*" element={<ProjectDetail />} />

      {/* Reseller-only routes — gate with Navigate if not reseller */}
      <Route
        path="sub-customers"
        element={
          isReseller ? (
            <Container size="xl">
              <SubCustomersTab canManage={isAdmin} />
            </Container>
          ) : (
            <Navigate to={`${ORG_BASE}/overview`} replace />
          )
        }
      />
      <Route
        path="admins"
        element={
          isReseller ? (
            <Container size="xl">
              <AdminsTab
                canManage={isAdmin}
                selfUserUuid={me?.user_uuid}
                selfContactId={me?.contact_id}
              />
            </Container>
          ) : (
            <Navigate to={`${ORG_BASE}/overview`} replace />
          )
        }
      />

      <Route index element={<Navigate to="overview" replace />} />
      <Route path="*" element={<Navigate to="overview" replace />} />
    </Routes>
  );
}

export default OrganizationsConsole;
